import { Component, inject, signal, computed, OnDestroy, OnInit } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink, ActivatedRoute } from '@angular/router';
import { ApiService, showToast } from './api.service';
import { TranslatePipe } from './i18n.service';

@Component({
  selector: 'app-job-execution',
  standalone: true,
  imports: [RouterLink, CommonModule, DatePipe, FormsModule, TranslatePipe],
  template: `
    <div class="min-h-screen flex flex-col pb-20" style="background:#F7FBFD;">
      <!-- Header -->
      <header class="px-5 pt-4 pb-4 flex items-center gap-3 sticky top-0 z-20 text-white shadow-sm"
              style="background:linear-gradient(135deg,#0F334A 0%,#145B7A 100%);">
        <a routerLink="/" class="w-10 h-10 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center text-white/90 active:bg-white/15 backdrop-blur">
          <i class="ti ti-arrow-left text-[20px]"></i>
        </a>
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2">
            <div class="text-[10px] font-medium text-white/55 uppercase tracking-widest">{{ job()?.job_id || 'Job' }}</div>
            @if (job()?.job_type) {
              <span class="text-[9px] font-medium px-2 py-0.5 rounded-full uppercase border backdrop-blur"
                    [class.bg-sky-400/15]="job()?.job_type === 'Inspection'"
                    [class.text-sky-100]="job()?.job_type === 'Inspection'"
                    [class.border-sky-200/20]="job()?.job_type === 'Inspection'"
                    [class.bg-emerald-400/15]="job()?.job_type === 'Service'"
                    [class.text-emerald-100]="job()?.job_type === 'Service'"
                    [class.border-emerald-200/25]="job()?.job_type === 'Service'"
                    [class.bg-rose-400/15]="job()?.job_type === 'Emergency'"
                    [class.text-rose-100]="job()?.job_type === 'Emergency'"
                    [class.border-rose-200/25]="job()?.job_type === 'Emergency'">
                {{ job()?.job_type }}
              </span>
            }
          </div>
          <h1 class="font-semibold text-base leading-tight text-white/95 truncate">{{ job()?.customer || 'Loading...' }}</h1>
        </div>
      </header>

      @if (isLoading()) {
        <main class="p-8 text-center flex flex-col items-center justify-center flex-1">
          <div class="w-14 h-14 rounded-2xl bg-white border border-slate-100 flex items-center justify-center text-[#1A6FA6] mb-4">
            <i class="ti ti-loader animate-spin text-[32px]"></i>
          </div>
          <h2 class="text-lg font-semibold text-slate-900 mb-2">{{ 'loadingJob' | t }}</h2>
          <p class="text-sm text-slate-500">Fetching the latest field details...</p>
        </main>
      } @else if (job()) {
        <main class="px-4 pt-5 space-y-4">

          <!-- Inspection Mode Banner -->
          @if (job()?.job_type === 'Inspection') {
            <div class="bg-sky-50 border border-sky-100 rounded-2xl px-4 py-3 flex items-center gap-3">
              <div class="w-9 h-9 rounded-xl bg-white text-[#1A6FA6] border border-sky-100 flex items-center justify-center shrink-0">
                <i class="ti ti-clipboard-search text-[20px]"></i>
              </div>
              <div>
                <p class="font-semibold text-sky-800 text-sm">Inspection Mode</p>
                <p class="text-xs text-sky-600 mt-0.5">Record observations, estimate materials and prepare the quote.</p>
              </div>
            </div>
          }

          <!-- Emergency Banner -->
          @if (job()?.job_type === 'Emergency') {
            <div class="bg-rose-50 border border-rose-100 rounded-2xl px-4 py-3 flex items-center gap-3">
              <div class="w-9 h-9 rounded-xl bg-white text-rose-600 border border-rose-100 flex items-center justify-center shrink-0">
                <i class="ti ti-alert-triangle text-[20px]"></i>
              </div>
              <div>
                <p class="font-semibold text-rose-800 text-sm">Emergency Job - Urgent</p>
                <p class="text-xs text-rose-600 mt-0.5">Start the timer before work begins.</p>
              </div>
            </div>
          }

          <!-- Address Block -->
          <div class="bg-white rounded-2xl px-4 py-3 border border-slate-100">
            <div class="flex gap-3 items-start mb-3">
              <i class="ti ti-map-pin text-[#1A6FA6] mt-0.5 text-lg shrink-0"></i>
              <p class="text-slate-600 font-normal leading-snug text-sm">{{ job()?.address }}</p>
            </div>
            <a [href]="'https://maps.google.com/?q=' + encodedAddress()" target="_blank"
               class="flex items-center justify-center gap-2 w-full bg-[#102C41] text-white h-11 rounded-xl font-semibold text-sm transition-all active:scale-[0.98]">
              <i class="ti ti-car text-lg"></i>
              {{ 'getDirections' | t }}
            </a>
          </div>

          <!-- Customer & Description -->
          @if (job()?.scheduled_date) {
            <div class="bg-white rounded-2xl px-4 py-3 border border-slate-100 flex items-center gap-3">
              <div class="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                   [class.bg-violet-50]="job()?.schedule_change_count > 0"
                   [class.text-violet-600]="job()?.schedule_change_count > 0"
                   [class.bg-sky-50]="!job()?.schedule_change_count"
                   [class.text-[#1A6FA6]]="!job()?.schedule_change_count">
                <i class="ti" [class.ti-calendar-time]="job()?.schedule_change_count > 0" [class.ti-calendar-check]="!job()?.schedule_change_count"></i>
              </div>
              <div class="min-w-0">
                <p class="text-[10px] font-semibold uppercase tracking-widest" [class.text-violet-600]="job()?.schedule_change_count > 0" [class.text-slate-400]="!job()?.schedule_change_count">
                  {{ job()?.schedule_change_count > 0 ? 'Rescheduled Time' : 'Scheduled Time' }}
                </p>
                <p class="text-sm font-semibold text-slate-900">{{ job()?.scheduled_date | date:'d MMM yyyy, h:mm a' }}</p>
                @if (job()?.schedule_change_count > 0) {
                  <p class="text-xs text-violet-700 mt-0.5">Latest confirmed time</p>
                }
              </div>
            </div>
          }

          <!-- Customer & Description -->
          <div class="bg-white rounded-2xl p-4 border border-slate-100">
            <div class="flex items-start gap-3 mb-3">
              <div class="w-9 h-9 rounded-xl bg-sky-50 text-[#1A6FA6] flex items-center justify-center shrink-0">
                <i class="ti ti-user-circle text-[20px]"></i>
              </div>
              <div class="flex-1">
                <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">{{ 'customer' | t }}</p>
                <p class="font-semibold text-slate-900 text-sm">{{ job()?.customer }}</p>
                @if (job()?.customerPhone) {
                  <a [href]="'tel:' + job()?.customerPhone" class="text-[#1A6FA6] text-sm font-medium flex items-center gap-1 mt-0.5">
                    <i class="ti ti-phone text-sm"></i> {{ job()?.customerPhone }}
                  </a>
                }
              </div>
            </div>
            @if (job()?.description) {
              <div class="bg-slate-50 rounded-2xl p-4 border border-slate-100">
                <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-1.5">{{ 'description' | t }}</p>
                <p class="text-sm text-slate-600 leading-relaxed">{{ job()?.description }}</p>
              </div>
            }

            <!-- Team Assignment -->
            @if (job()?.assigned_workers?.length > 0) {
              <div class="mt-4 pt-4 border-t border-slate-100">
                <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-2">Team Assigned</p>
                <div class="flex flex-wrap gap-2">
                  @for (worker of job()?.assigned_workers; track worker.id) {
                    <div class="px-3 py-1.5 bg-sky-50 border border-sky-100 rounded-full flex items-center gap-2">
                      <div class="w-5 h-5 rounded-full bg-[#1A6FA6] text-white flex items-center justify-center text-[10px] font-semibold">{{ worker.name.charAt(0) }}</div>
                      <span class="text-xs font-medium text-sky-700">{{ worker.name }}</span>
                    </div>
                  }
                </div>
              </div>
            }
          </div>

          <!-- Started / Completed timestamps -->
          @if (job()?.started_at) {
            <div class="flex items-center gap-3 bg-emerald-50 border border-emerald-100 rounded-2xl px-4 py-3">
              <i class="ti ti-player-play-filled text-emerald-500 text-[20px] shrink-0"></i>
              <div>
                <p class="text-[10px] font-semibold text-emerald-600 uppercase tracking-widest">Job Started</p>
                <p class="text-sm font-medium text-emerald-800">{{ job()?.started_at | date:'d MMM yyyy, h:mm a' }}</p>
              </div>
            </div>
          }
          @if (job()?.completed_at) {
            <div class="flex items-center gap-3 bg-sky-50 border border-sky-100 rounded-2xl px-4 py-3">
              <i class="ti ti-flag-check text-sky-500 text-lg shrink-0"></i>
              <div>
                <p class="text-[10px] font-semibold text-sky-600 uppercase tracking-widest">Field Work Finished</p>
                <p class="text-sm font-medium text-sky-800">{{ job()?.completed_at | date:'d MMM yyyy, h:mm a' }}</p>
                @if (job()?.actual_hours !== null && job()?.actual_hours !== undefined) {
                  <p class="text-xs font-semibold text-sky-700 mt-0.5">{{ job()?.actual_hours | number:'1.2-2' }} actual hour(s) recorded</p>
                }
              </div>
            </div>
          }

          <!-- ═══ TIMER / STATUS BUTTON ═══ -->
          @if (!shiftActive() && canUseWorkerTimer() && !isTimerActive()) {
            <div class="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 flex items-center gap-2 text-xs text-amber-900">
              <i class="ti ti-alert-triangle-filled text-amber-600 shrink-0"></i>
              <span class="font-semibold flex-1">Please start your shift before starting or resuming this job.</span>
              <a routerLink="/" class="font-bold text-amber-700 underline underline-offset-2 whitespace-nowrap">Start Shift</a>
            </div>
          }

          @if (job()?.status === 'Paused') {
            <div class="flex items-center gap-3 bg-orange-50 border border-orange-100 rounded-2xl px-4 py-3">
              <i class="ti ti-player-pause-filled text-orange-500 text-[20px] shrink-0"></i>
              <div class="min-w-0">
                <p class="text-[10px] font-semibold text-orange-600 uppercase tracking-widest">Job Paused</p>
                <p class="text-sm font-medium text-orange-800">{{ pausedDateLabel() }}</p>
                @if (job()?.pause_category || job()?.pause_remarks) {
                  <p class="text-xs text-orange-700 mt-1 truncate">{{ job()?.pause_category || 'Paused' }}: {{ job()?.pause_remarks || 'No remarks recorded' }}</p>
                }
              </div>
            </div>
          }

          @if (job()?.status === 'Completed') {
            <div class="w-full h-11 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 bg-emerald-50 border border-emerald-100 text-emerald-700">
              <i class="ti ti-circle-check-filled text-lg"></i>
              {{ 'completed' | t }}
            </div>
          } @else if (job()?.status === 'Ready for Office') {
            <div class="w-full h-11 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 bg-blue-50 border border-blue-100 text-blue-700">
              <i class="ti ti-send-check text-lg"></i>
              {{ 'readyForOffice' | t }}
            </div>
          } @else if (job()?.status === 'Terminated') {
            <div class="w-full h-11 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 bg-rose-50 border border-rose-100 text-rose-700">
              <i class="ti ti-circle-x-filled text-lg"></i>
              JOB TERMINATED
            </div>
          } @else if (job()?.job_type !== 'Inspection' && canUseWorkerTimer()) {
            <!-- Service + Emergency: full timer -->
            <button
              (click)="onTimerClick()"
              [disabled]="isUpdating() || (!isTimerActive() && !shiftActive())"
              class="w-full h-12 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all active:scale-[0.98] relative overflow-hidden group disabled:opacity-50">
              @if (!isTimerActive()) {
                <div class="absolute inset-0"
                     [style.background]="job()?.job_type === 'Emergency' ? 'linear-gradient(135deg,#B91C1C,#F43F5E)' : 'linear-gradient(135deg,#0F334A,#145B7A)'"></div>
              } @else {
                <div class="absolute inset-0 bg-gradient-to-r from-red-500 to-rose-400"></div>
                <div class="absolute inset-0 bg-white opacity-20 animate-pulse"></div>
              }
              <div class="relative z-10 flex items-center gap-2 text-white">
                @if (isUpdating()) {
                  <i class="ti ti-loader animate-spin text-lg"></i> UPDATING...
                } @else if (isTimerActive()) {
                  <i class="ti ti-player-stop-filled text-lg"></i>
                  <div class="flex flex-col items-start leading-none mt-0.5">
                    <span class="text-[9px] font-medium opacity-90 uppercase tracking-widest mb-0.5">Stop my timer</span>
                    <span class="font-mono tracking-wider text-lg leading-none">{{ formatTime(timerSeconds()) }}</span>
                  </div>
                } @else if (job()?.active_session?.status === 'paused') {
                  <i class="ti ti-player-play-filled text-lg"></i> RESUME MY TIMER
                } @else {
                  <i class="ti ti-player-play-filled text-lg"></i>
                  {{ workerStartButtonLabel() }}
                }
              </div>
            </button>
            @if (job()?.pricing_type === 'Fixed') {
              <div class="mt-2 rounded-xl bg-violet-50 border border-violet-100 px-3 py-2 text-xs text-violet-800 flex items-start gap-2">
                <i class="ti ti-cash mt-0.5"></i>
                <span>Fixed-rate job: timer is for internal audit only. Labour quote uses the fixed charge.</span>
              </div>
            }
            @if (job()?.job_type === 'Emergency' && job()?.emergency_double_rate === 1) {
              <div class="mt-2 rounded-xl bg-rose-50 border border-rose-100 px-3 py-2 text-xs text-rose-700 flex items-start gap-2">
                <i class="ti ti-bolt mt-0.5"></i><span>Double emergency labour rate applies. Recorded hours are unchanged and materials remain at normal pricing.</span>
              </div>
            }
            @if (isTimerActive()) {
              <button
                (click)="pauseJob()"
                [disabled]="isUpdating()"
                class="w-full h-11 mt-2 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 bg-orange-50 border border-orange-100 text-orange-700 transition-all active:scale-[0.98] disabled:opacity-50">
                @if (isUpdating()) {
                  <i class="ti ti-loader animate-spin text-lg"></i> PAUSING...
                } @else {
                  <i class="ti ti-player-pause-filled text-lg"></i> PAUSE MY TIMER
                }
              </button>
            }
            @if (job()?.active_worker_count > 0 || job()?.paused_worker_count > 0) {
              <div class="mt-2 rounded-xl bg-sky-50 border border-sky-100 px-3 py-2 text-xs text-sky-800">
                <strong>{{ job()?.active_worker_count || 0 }} worker(s) timing now</strong>
                @if (job()?.paused_worker_count > 0) { <span> · {{ job()?.paused_worker_count }} paused</span> }
                <span class="block mt-0.5">Your controls affect only your own time. The job updates automatically when the team pauses or stops.</span>
              </div>
            }
          } @else if (job()?.job_type !== 'Inspection') {
            <div class="w-full rounded-xl font-semibold text-sm flex items-center justify-center gap-2 bg-slate-50 border border-slate-100 text-slate-600 px-4 py-3 text-center">
              <i class="ti ti-info-circle text-lg"></i>
              {{ job()?.status === 'Unassigned' ? 'Waiting for office assignment' : 'Waiting for office approval or scheduling' }}
            </div>
          }

          @if (reviewActionRequired()) {
            <section class="rounded-2xl border border-amber-200 bg-amber-50 p-4">
              <div class="flex items-start gap-3">
                <div class="w-10 h-10 rounded-xl bg-white border border-amber-100 text-amber-600 flex items-center justify-center shrink-0">
                  <i class="ti ti-message-star text-xl"></i>
                </div>
                <div class="min-w-0 flex-1">
                  <p class="text-sm font-semibold text-amber-950">Customer review request pending</p>
                  <p class="text-xs text-amber-800 mt-1">Send the one-click rating request to {{ job()?.customer || 'the customer' }}.</p>
                  @if (job()?.review_request?.reminder_requested_at) {
                    <p class="text-[11px] font-semibold text-amber-700 mt-2"><i class="ti ti-bell"></i> Reminder is active for you and admin.</p>
                  }
                </div>
              </div>
              <div class="grid grid-cols-2 gap-2 mt-4">
                <button type="button" (click)="remindReviewLater()" [disabled]="isReviewActionSaving()"
                        class="h-11 rounded-xl border border-amber-200 bg-white text-amber-800 text-xs font-semibold disabled:opacity-60">
                  Remind Me Later
                </button>
                <button type="button" (click)="sendReviewRequest()" [disabled]="isReviewActionSaving()"
                        class="h-11 rounded-xl bg-[#102C41] text-white text-xs font-semibold disabled:opacity-60 flex items-center justify-center gap-2">
                  @if (isReviewActionSaving()) { <i class="ti ti-loader-2 animate-spin"></i> } @else { <i class="ti ti-send"></i> }
                  Send Review Request
                </button>
              </div>
            </section>
          }

          <!-- ═══ ACTION LIST ═══ -->
          @if (showReviewPrompt()) {
            <div class="fixed inset-0 z-[90] overflow-y-auto p-4 flex items-center justify-center" style="background:rgba(15,23,42,0.55);">
              <div class="bg-white rounded-3xl border border-slate-100 p-5 w-full max-w-[390px] shadow-xl">
                <div class="w-14 h-14 rounded-2xl mx-auto bg-sky-50 text-[#1A6FA6] flex items-center justify-center">
                  <i class="ti ti-message-star text-3xl"></i>
                </div>
                <h2 class="text-xl font-semibold text-slate-900 text-center mt-4">Send customer review request?</h2>
                <p class="text-sm text-slate-500 text-center mt-2 leading-relaxed">Field work is finished. Send {{ job()?.customer || 'the customer' }} the NZ Plumbing star-rating request now.</p>
                <label class="block mt-4">
                  <span class="block text-xs font-semibold text-slate-600 mb-1.5">Customer email</span>
                  <input type="email" inputmode="email" autocomplete="email" [(ngModel)]="reviewCustomerEmail" (ngModelChange)="reviewEmailError = ''"
                         placeholder="customer@example.com"
                         class="w-full h-11 px-3 rounded-xl border bg-slate-50 text-sm outline-none focus:bg-white focus:border-[#1A6FA6]"
                         [class.border-rose-300]="reviewEmailError" [class.border-slate-200]="!reviewEmailError">
                  @if (reviewEmailError) {
                    <p class="text-xs text-rose-600 mt-1.5">{{ reviewEmailError }}</p>
                  } @else {
                    <p class="text-[11px] text-slate-400 mt-1.5">This email is saved to the customer record for future communication.</p>
                  }
                </label>
                <button type="button" (click)="sendReviewRequest()" [disabled]="isReviewActionSaving()"
                        class="w-full h-12 mt-5 rounded-xl bg-[#102C41] text-white text-sm font-semibold disabled:opacity-60 flex items-center justify-center gap-2">
                  @if (isReviewActionSaving()) { <i class="ti ti-loader-2 animate-spin"></i> Sending... } @else { <i class="ti ti-send"></i> Send Review Request }
                </button>
                <button type="button" (click)="remindReviewLater()" [disabled]="isReviewActionSaving()"
                        class="w-full h-11 mt-2 rounded-xl border border-slate-200 bg-slate-50 text-slate-600 text-sm font-semibold disabled:opacity-60">
                  Remind Me Later
                </button>
                <p class="text-[11px] text-slate-400 text-center mt-3">Choosing Remind Me Later notifies you and the admin team until this is sent.</p>
              </div>
            </div>
          }

          @if (showSafetyConfirm()) {
            <div class="fixed inset-0 z-[80] overflow-y-auto p-4 flex items-start justify-center" style="background:rgba(15,23,42,0.45);">
              <div class="bg-white rounded-2xl border border-slate-100 p-5 w-full max-w-[380px] my-4 max-h-[calc(100dvh-2rem)] overflow-y-auto">
                <div class="w-12 h-12 rounded-2xl mx-auto mb-4 bg-emerald-50 text-emerald-600 flex items-center justify-center">
                  <i class="ti ti-shield-check text-2xl"></i>
                </div>
                <h2 class="text-lg font-semibold text-slate-900 text-center">Safety Compliance</h2>
                <p class="text-sm text-slate-600 leading-relaxed mt-3">{{ safetyStatement }}</p>
                <label class="flex items-start gap-3 mt-4 p-3 rounded-xl bg-slate-50 border border-slate-100">
                  <input type="checkbox" [checked]="safetyAccepted()" (change)="safetyAccepted.set($any($event.target).checked)" class="mt-1">
                  <span class="text-sm text-slate-600">I have reviewed and accept this safety acknowledgement before starting work.</span>
                </label>
                <div class="mt-4 rounded-xl bg-rose-50 border border-rose-100 p-3 space-y-3">
                  <div>
                    <p class="text-xs font-semibold text-rose-800">Cannot accept?</p>
                    <p class="text-xs text-rose-600 mt-0.5">Record the safety concern before work starts. This will not start the job.</p>
                  </div>
                  <textarea [(ngModel)]="safetyDeclineReason" rows="3" placeholder="Explain why this site is not safe to start..."
                            class="w-full px-3 py-2 rounded-xl border border-rose-100 bg-white outline-none text-sm text-slate-700 resize-none"></textarea>
                  <input #safetyPhotoInput type="file" accept="image/*" capture="environment" class="hidden" (change)="onSafetyPhotoSelected($event)">
                  @if (safetyDeclinePhoto()) {
                    <div class="flex items-center gap-3 rounded-xl bg-white border border-rose-100 p-2">
                      <img [src]="safetyDeclinePhoto()" alt="Safety concern photo" class="w-14 h-14 rounded-lg object-cover">
                      <div class="flex-1 min-w-0">
                        <p class="text-xs font-semibold text-slate-700 truncate">{{ safetyDeclinePhotoName() || 'Photo attached' }}</p>
                        <button type="button" (click)="clearSafetyPhoto()" class="text-xs font-semibold text-rose-600 mt-1">Remove photo</button>
                      </div>
                    </div>
                  } @else {
                    <button type="button" (click)="safetyPhotoInput.click()" class="w-full h-10 rounded-xl bg-white border border-rose-100 text-rose-700 text-sm font-semibold flex items-center justify-center gap-2">
                      <i class="ti ti-camera"></i> Add Photo
                    </button>
                  }
                </div>
                <div class="sticky bottom-0 -mx-5 -mb-5 mt-5 bg-white px-5 pb-5 pt-3 border-t border-slate-100 rounded-b-2xl">
                  <div class="grid grid-cols-2 gap-3">
                    <button type="button" (click)="closeSafetyConfirm()" class="h-11 rounded-xl bg-slate-50 border border-slate-100 text-slate-600 font-semibold text-sm">Cancel</button>
                    <button type="button" (click)="confirmSafetyAndStart()" [disabled]="!safetyAccepted() || isUpdating()"
                            class="h-11 rounded-xl bg-emerald-600 text-white font-semibold text-sm disabled:opacity-60">
                      @if (isUpdating()) { <i class="ti ti-loader animate-spin"></i> } @else { Accept & Start }
                    </button>
                  </div>
                  <button type="button" (click)="declineSafetyAcknowledgement()" [disabled]="!safetyDeclineReason.trim() || isUpdating()"
                          class="w-full h-11 rounded-xl bg-rose-600 text-white font-semibold text-sm disabled:opacity-60 mt-3 flex items-center justify-center gap-2">
                    @if (isUpdating()) { <i class="ti ti-loader animate-spin"></i> } @else { <i class="ti ti-shield-x"></i> Decline & Send to Admin }
                  </button>
                </div>
              </div>
            </div>
          }

          @if (showPauseModal()) {
            <div class="fixed inset-0 z-[80] overflow-y-auto p-4 flex items-start justify-center" style="background:rgba(15,23,42,0.45);">
              <div class="bg-white rounded-2xl border border-slate-100 p-5 w-full max-w-[380px] my-4">
                <div class="w-12 h-12 rounded-2xl mx-auto mb-4 bg-orange-50 text-orange-600 flex items-center justify-center">
                  <i class="ti ti-player-pause text-2xl"></i>
                </div>
                <h2 class="text-lg font-semibold text-slate-900 text-center">Pause my timer</h2>
                <p class="text-sm text-slate-500 text-center mt-1">Other workers can continue. The job pauses only when everyone has paused.</p>
                <label class="block mt-4">
                  <span class="text-xs font-semibold text-slate-600 mb-1 block">Reason</span>
                  <select [(ngModel)]="pauseCategory" class="w-full h-11 px-3 rounded-xl border border-slate-200 bg-slate-50 outline-none text-sm">
                    @for (category of pauseCategories; track category) {
                      <option [value]="category">{{ category }}</option>
                    }
                  </select>
                </label>
                <label class="block mt-3">
                  <span class="text-xs font-semibold text-slate-600 mb-1 block">Remarks</span>
                  <textarea [(ngModel)]="pauseRemarks" rows="4" placeholder="Example: waiting for mixer cartridge from supplier..."
                            class="w-full px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 outline-none text-sm text-slate-700 resize-none"></textarea>
                </label>
                <div class="grid grid-cols-2 gap-3 mt-5">
                  <button type="button" (click)="closePauseModal()" class="h-11 rounded-xl bg-slate-50 border border-slate-100 text-slate-600 font-semibold text-sm">Cancel</button>
                  <button type="button" (click)="confirmPauseJob()" [disabled]="isUpdating() || pauseRemarks.trim().length < 5"
                          class="h-11 rounded-xl bg-orange-600 text-white font-semibold text-sm disabled:opacity-60">
                    @if (isUpdating()) { Pausing... } @else { Pause my timer }
                  </button>
                </div>
              </div>
            </div>
          }

          @if (showResumeModal()) {
            <div class="fixed inset-0 z-[80] overflow-y-auto p-4 flex items-start justify-center" style="background:rgba(15,23,42,0.45);">
              <div class="bg-white rounded-2xl border border-slate-100 p-5 w-full max-w-[380px] my-4">
                <div class="w-12 h-12 rounded-2xl mx-auto mb-4 bg-emerald-50 text-emerald-600 flex items-center justify-center">
                  <i class="ti ti-player-play text-2xl"></i>
                </div>
                <h2 class="text-lg font-semibold text-slate-900 text-center">Resume Job</h2>
                <p class="text-sm text-slate-500 text-center mt-1">Record what cleared the pause.</p>
                @if (job()?.pause_category || job()?.pause_remarks) {
                  <div class="mt-4 rounded-xl bg-orange-50 border border-orange-100 px-3 py-2">
                    <p class="text-[10px] font-bold uppercase tracking-widest text-orange-600">{{ job()?.pause_category || 'Pause Reason' }}</p>
                    <p class="text-xs font-semibold text-orange-800 mt-0.5">{{ job()?.pause_remarks || 'No remarks recorded' }}</p>
                  </div>
                }
                <label class="block mt-4">
                  <span class="text-xs font-semibold text-slate-600 mb-1 block">Resolution note</span>
                  <textarea [(ngModel)]="pauseResolutionNotes" rows="4" placeholder="Example: parts collected, customer onsite, work can continue..."
                            class="w-full px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 outline-none text-sm text-slate-700 resize-none"></textarea>
                </label>
                <div class="grid grid-cols-2 gap-3 mt-5">
                  <button type="button" (click)="closeResumeModal()" class="h-11 rounded-xl bg-slate-50 border border-slate-100 text-slate-600 font-semibold text-sm">Cancel</button>
                  <button type="button" (click)="confirmResumePausedJob()" [disabled]="isUpdating() || pauseResolutionNotes.trim().length < 5"
                          class="h-11 rounded-xl bg-emerald-600 text-white font-semibold text-sm disabled:opacity-60">
                    @if (isUpdating()) { Resuming... } @else { Resume }
                  </button>
                </div>
              </div>
            </div>
          }

          <div class="space-y-3">

            <!-- Photos -->
            <a [routerLink]="canUseJobTools() ? ['/job', job()?.job_id, 'photos'] : null"
               class="w-full bg-white p-4 rounded-2xl border border-slate-100 flex items-center gap-3 transition-all active:scale-[0.98]"
               [class.opacity-50]="!canUseJobTools()"
               [class.pointer-events-none]="!canUseJobTools()">
              <div class="w-10 h-10 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center shrink-0">
                <i class="ti ti-camera text-xl"></i>
              </div>
              <div class="flex-1 text-left">
                <h3 class="font-semibold text-slate-800 text-sm leading-tight">Capture Photos</h3>
                <p class="text-xs text-slate-500 font-medium mt-0.5">Document site conditions</p>
              </div>
              <i class="ti ti-chevron-right text-slate-300"></i>
            </a>

            <!-- Notes -->
            <a [routerLink]="['/job', job()?.job_id, 'notes']"
               class="w-full bg-white p-4 rounded-2xl border border-slate-100 flex items-center gap-3 transition-all active:scale-[0.98]">
              <div class="w-10 h-10 rounded-xl bg-orange-50 text-orange-600 flex items-center justify-center shrink-0">
                <i class="ti ti-notes text-xl"></i>
              </div>
              <div class="flex-1 text-left">
                <h3 class="font-semibold text-slate-800 text-sm leading-tight">
                  {{ job()?.job_type === 'Inspection' ? 'Inspection Notes' : 'Job Notes' }}
                </h3>
                <p class="text-xs text-slate-500 font-medium mt-0.5">
                  {{ job()?.job_type === 'Inspection' ? 'Record site observations & findings' : 'Add site observations' }}
                </p>
              </div>
              <i class="ti ti-chevron-right text-slate-300"></i>
            </a>

            <!-- Materials -->
            <a [routerLink]="canUseJobTools() ? ['/job', job()?.job_id, 'materials'] : null"
               class="w-full bg-white p-4 rounded-2xl border border-slate-100 flex items-center gap-3 transition-all active:scale-[0.98]"
               [class.opacity-50]="!canUseJobTools()"
               [class.pointer-events-none]="!canUseJobTools()">
              <div class="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
                <i class="ti ti-box text-xl"></i>
              </div>
              <div class="flex-1 text-left">
                <h3 class="font-semibold text-slate-800 text-sm leading-tight">
                  {{ job()?.job_type === 'Inspection' ? 'Materials Estimate' : 'Job Materials' }}
                </h3>
                <p class="text-xs text-slate-500 font-medium mt-0.5">{{ job()?.materialsCount || 0 }} items assigned</p>
              </div>
              @if (job()?.status !== 'Completed' && job()?.status !== 'Terminated') {
                <div class="w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center text-slate-500 active:bg-slate-100 border border-slate-100"><i class="ti ti-plus"></i></div>
              } @else {
                <i class="ti ti-chevron-right text-slate-300"></i>
              }
            </a>

            <!-- Service job: View Inspection Quote (if linked) -->
            @if (job()?.job_type === 'Service' && job()?.linked_inspection_id) {
              <a [routerLink]="['/job', job()?.linked_inspection_id, 'quote']"
                 [queryParams]="{ return_job_id: job()?.job_id }"
                 class="w-full bg-white p-4 rounded-2xl border border-sky-100 flex items-center gap-3 transition-all active:scale-[0.98]">
                <div class="w-10 h-10 rounded-xl bg-sky-50 text-[#1A6FA6] flex items-center justify-center shrink-0">
                  <i class="ti ti-clipboard-search text-xl"></i>
                </div>
                <div class="flex-1 text-left">
                  <h3 class="font-semibold text-slate-800 text-sm leading-tight">View Inspection Quote</h3>
                  <p class="text-xs text-sky-600 font-medium mt-0.5">Prepared during site inspection</p>
                </div>
                <i class="ti ti-chevron-right text-slate-300"></i>
              </a>
            }

            <!-- Build/View Quote -->
            <a [routerLink]="canUseJobTools() ? ['/job', job()?.job_id, 'quote'] : null"
               class="w-full p-4 rounded-2xl flex items-center gap-3 transition-all active:scale-[0.98]"
               [class.opacity-50]="!canUseJobTools()"
               [class.pointer-events-none]="!canUseJobTools()"
               [style.background]="!job()?.hasQuote && job()?.status !== 'Completed' ? 'linear-gradient(135deg, #0F334A, #145B7A)' : '#ffffff'"
               [style.border]="job()?.hasQuote || job()?.status === 'Completed' ? '1px solid #E2E8F0' : '1px solid rgba(255,255,255,0.12)'">

              <div class="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border"
                   [class.bg-white/20]="!job()?.hasQuote && job()?.status !== 'Completed'"
                   [class.border-white/10]="!job()?.hasQuote && job()?.status !== 'Completed'"
                   [class.text-white]="!job()?.hasQuote && job()?.status !== 'Completed'"
                   [class.bg-sky-50]="job()?.hasQuote || job()?.status === 'Completed'"
                   [class.border-[#E4F1FA]]="job()?.hasQuote || job()?.status === 'Completed'"
                   [class.text-[#1A6FA6]]="job()?.hasQuote || job()?.status === 'Completed'">
                <i class="ti ti-file-invoice text-xl"></i>
              </div>

              <div class="flex-1 text-left">
                <h3 class="font-semibold text-sm leading-tight"
                    [class.text-white]="!job()?.hasQuote && job()?.status !== 'Completed'"
                    [class.text-slate-900]="job()?.hasQuote || job()?.status === 'Completed'">
                  {{ job()?.hasQuote || job()?.status === 'Completed' ? 'View Quote' : (job()?.job_type === 'Inspection' ? 'Build Quote' : 'Build Quote') }}
                </h3>
                <p class="text-xs font-medium mt-0.5"
                   [class.text-[#E4F1FA]]="!job()?.hasQuote && job()?.status !== 'Completed'"
                   [class.text-slate-500]="job()?.hasQuote || job()?.status === 'Completed'">
                  {{ job()?.hasQuote || job()?.status === 'Completed' ? 'Finalized document' : 'Generate pricing document' }}
                </p>
              </div>
              <i class="ti ti-chevron-right"
                 [class.text-white]="!job()?.hasQuote && job()?.status !== 'Completed'"
                 [class.text-slate-300]="job()?.hasQuote || job()?.status === 'Completed'"></i>
            </a>

          </div>
        </main>
      } @else {
        <main class="p-8 text-center flex flex-col items-center justify-center flex-1">
          <div class="w-14 h-14 rounded-2xl bg-white border border-rose-100 flex items-center justify-center text-rose-400 mb-4">
            <i class="ti ti-alert-circle text-[32px]"></i>
          </div>
          <h2 class="text-lg font-semibold text-slate-900 mb-2">{{ loadError() || 'Job Not Found' }}</h2>
          <p class="text-sm text-slate-500">The job may have been removed or is no longer assigned to your account.</p>
          <a routerLink="/" class="mt-6 h-11 px-6 rounded-xl bg-[#102C41] text-white font-semibold text-sm flex items-center justify-center">
            Back to Jobs
          </a>
        </main>
      }
    </div>
  `
})
export class JobExecutionComponent implements OnInit, OnDestroy {
  api = inject(ApiService);
  route = inject(ActivatedRoute);

  job = signal<any | undefined>(undefined);
  isLoading = signal(true);
  loadError = signal('');
  encodedAddress = computed(() => encodeURIComponent(this.job()?.address || ''));

  isTimerActive = signal(false);
  shiftActive = this.api.currentShiftActive;
  shiftStatusLoading = signal(true);
  timerSeconds = signal(0);
  isUpdating = signal(false);
  showSafetyConfirm = signal(false);
  showReviewPrompt = signal(false);
  isReviewActionSaving = signal(false);
  reviewCustomerEmail = '';
  reviewEmailError = '';
  safetyAccepted = signal(false);
  safetyDeclinePhoto = signal<string | null>(null);
  safetyDeclinePhotoName = signal('');
  showPauseModal = signal(false);
  showResumeModal = signal(false);
  safetyDeclineReason = '';
  pauseCategories = ['Waiting for Parts', 'Customer Not Available', 'Unsafe Site', 'Need Approval', 'Other'];
  pauseCategory = 'Waiting for Parts';
  pauseRemarks = '';
  pauseResolutionNotes = '';
  safetyStatement = 'Before starting work, I confirm that I have assessed the site and will work in accordance with NZ Plumbing safety procedures and New Zealand Health and Safety at Work expectations. I will take reasonable care for my own health and safety, ensure my actions do not put other people at risk, follow reasonable instructions and policies, use required PPE, and stop work or raise concerns if conditions are unsafe.';
  private intervalId: ReturnType<typeof setInterval> | undefined;
  private readonly handleVisibilityChange = () => {
    if (!document.hidden) {
      this.refreshTimerFromJob();
      this.loadShiftStatus();
    }
  };

  ngOnInit() {
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      if (id) {
        this.loadJob(id);
      }
    });
  }

  loadJob(id: string) {
    this.isLoading.set(true);
    this.loadError.set('');
    this.isTimerActive.set(false);
    this.timerSeconds.set(0);
    this.stopTimerTicking();
    this.api.getJob(id).subscribe({
      next: (res: any) => {
        if (res.success && res.data) {
          const j = res.data;
          const customerContact = j.contacts?.find((c: any) => c.contact_type === 'Customer');
          const contactWithEmail = customerContact?.email ? customerContact : j.contacts?.find((c: any) => c.email);
          this.reviewCustomerEmail = contactWithEmail?.email || '';
          this.reviewEmailError = '';
          this.job.set({
            job_id: j.id,
            job_type: j.type || j.job_type || 'Service',
            customer: customerContact?.name || 'Anonymous',
            customerPhone: customerContact?.phone || null,
            customerEmail: contactWithEmail?.email || null,
            address: j.address || 'Unknown Address',
            description: j.desc || j.description || null,
            status: j.status,
            scheduled_date: j.scheduled_date || null,
            schedule_change_count: Number(j.schedule_change_count || 0),
            started_at: j.started_at || null,
            completed_at: j.completed_at || null,
            paused_at: j.paused_at || null,
            pause_started_at: j.pause_started_at || null,
            pause_category: j.pause_category || '',
            pause_remarks: j.pause_remarks || '',
            pause_resolution_notes: j.pause_resolution_notes || '',
            pause_resolved_at: j.pause_resolved_at || null,
            total_paused_seconds: Number(j.total_paused_seconds || 0),
            pricing_type: j.pricing_type || 'Hourly',
            fixed_labour_charge: Number(j.fixed_labour_charge || 0),
            emergency_double_rate: Number(j.emergency_double_rate || 0),
            actual_hours: j.actual_hours !== null && j.actual_hours !== undefined ? Number(j.actual_hours) : null,
            active_session: j.active_session || null,
            active_worker_count: Number(j.active_worker_count || 0),
            paused_worker_count: Number(j.paused_worker_count || 0),
            work_sessions: j.work_sessions || [],
            hourly_rate: j.assigned_workers?.[0]?.hourly_rate || 0,
            assigned_workers: j.assigned_workers || [],
            hasQuote: !!j.has_quote,
            photosCount: 0,
            notesCount: 0,
            materialsCount: j.materials?.length || 0,
            linked_inspection_id: j.linked_inspection_id || null,
            review_request: j.review_request || null,
          });

          // Only this worker's active session drives their timer.
          if (j.active_session?.status === 'active') {
            this.isTimerActive.set(true);
            this.startTimerTicking();
          } else {
            this.refreshTimerFromJob();
          }
        } else {
          this.job.set(undefined);
          this.loadError.set(res.message || 'Job Not Found');
        }
        this.isLoading.set(false);
      },
      error: (err: any) => {
        this.job.set(undefined);
        this.loadError.set(err?.error?.message || 'Unable to load job');
        this.isLoading.set(false);
      }
    });
  }

  onTimerClick() {
    if (!this.isTimerActive() && !this.shiftActive()) {
      showToast('error', 'Start your shift before starting or resuming your job timer.');
      return;
    }
    if (this.job()?.active_session?.status === 'paused') {
      this.resumePausedJob();
      return;
    }
    if (!this.isTimerActive()) {
      this.safetyAccepted.set(false);
      this.resetSafetyDecline();
      this.showSafetyConfirm.set(true);
      return;
    }
    this.toggleTimer();
  }

  closeSafetyConfirm() {
    if (this.isUpdating()) return;
    this.showSafetyConfirm.set(false);
    this.safetyAccepted.set(false);
    this.resetSafetyDecline();
  }

  confirmSafetyAndStart() {
    const currentId = this.job()?.job_id;
    if (!currentId || !this.safetyAccepted() || this.isUpdating()) return;
    this.isUpdating.set(true);
    this.api.acceptSafetyAcknowledgement({
      reference_id: currentId,
      model_type: 'Job',
      statement: this.safetyStatement
    }).subscribe({
      next: (res: any) => {
        if (!res.success) {
          this.isUpdating.set(false);
          showToast('error', res.message || 'Safety acknowledgement failed.');
          return;
        }
        this.showSafetyConfirm.set(false);
        this.isUpdating.set(false);
        this.toggleTimer();
      },
      error: () => {
        this.isUpdating.set(false);
        showToast('error', 'Network failure recording safety acknowledgement.');
      }
    });
  }

  declineSafetyAcknowledgement() {
    const currentId = this.job()?.job_id;
    const reason = this.safetyDeclineReason.trim();
    if (!currentId || !reason || this.isUpdating()) return;

    this.isUpdating.set(true);
    this.api.acceptSafetyAcknowledgement({
      reference_id: currentId,
      model_type: 'Job',
      statement: this.safetyStatement,
      status: 'Declined',
      decline_reason: reason,
      decline_photo_data: this.safetyDeclinePhoto()
    }).subscribe({
      next: (res: any) => {
        this.isUpdating.set(false);
        if (!res.success) {
          showToast('error', res.message || 'Safety decline could not be recorded.');
          return;
        }
        this.showSafetyConfirm.set(false);
        this.safetyAccepted.set(false);
        this.resetSafetyDecline();
        showToast('success', 'Safety concern sent to admin.');
        this.loadJob(currentId);
      },
      error: () => {
        this.isUpdating.set(false);
        showToast('error', 'Network failure recording safety concern.');
      }
    });
  }

  onSafetyPhotoSelected(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      this.safetyDeclinePhoto.set(reader.result as string);
      this.safetyDeclinePhotoName.set(file.name);
    };
    reader.readAsDataURL(file);
  }

  clearSafetyPhoto() {
    this.safetyDeclinePhoto.set(null);
    this.safetyDeclinePhotoName.set('');
  }

  private resetSafetyDecline() {
    this.safetyDeclineReason = '';
    this.clearSafetyPhoto();
  }

  toggleTimer() {
    if (this.isUpdating() || !this.canUseWorkerTimer()) return;

    this.isUpdating.set(true);
    const currentId = this.job().job_id;
    const action = this.isTimerActive() ? 'stop' : 'start';

    this.api.updateJobWorkSession({ job_id: currentId, action }).subscribe({
      next: (res: any) => {
        this.isUpdating.set(false);
        if (res.success) {
          const feedback = res?.data?.feedback_request;
          if (action === 'stop' && feedback?.action_required) {
            this.job.update(current => current ? { ...current, review_request: feedback } : current);
            this.showReviewPrompt.set(true);
            showToast('success', 'Job ended. The customer review request is ready to send.');
          } else {
            showToast('success', res.message || (action === 'start' ? 'Your timer started.' : 'Your timer stopped.'));
          }
          if (action === 'stop') {
            this.isTimerActive.set(false);
            this.stopTimerTicking();
          }
          this.loadJob(currentId);
        } else {
          showToast('error', res.message || 'Failed to update job status');
        }
      },
      error: (err: any) => {
        this.isUpdating.set(false);
        showToast('error', err?.error?.message || 'Network failure updating job status.');
      }
    });
    this.loadShiftStatus();
  }

  reviewActionRequired(): boolean {
    return !!this.job()?.review_request?.action_required;
  }

  sendReviewRequest() {
    const jobId = this.job()?.job_id;
    if (!jobId || this.isReviewActionSaving() || !this.reviewActionRequired()) return;
    const customerEmail = this.reviewCustomerEmail.trim().toLowerCase();
    if (!this.validReviewEmail(customerEmail)) {
      this.reviewEmailError = 'Enter a valid customer email address before sending.';
      this.showReviewPrompt.set(true);
      return;
    }
    this.reviewEmailError = '';
    this.isReviewActionSaving.set(true);
    this.api.sendJobReviewRequest(jobId, customerEmail).subscribe({
      next: (res: any) => {
        this.isReviewActionSaving.set(false);
        if (!res?.success) {
          showToast('error', res?.message || 'Review request could not be sent.');
          return;
        }
        this.showReviewPrompt.set(false);
        this.job.update(current => current ? { ...current, review_request: res.data?.review_request || { action_required: false, email_sent: true } } : current);
        showToast('success', 'Customer review request sent.');
        this.loadJob(jobId);
      },
      error: (err: any) => {
        this.isReviewActionSaving.set(false);
        if (err?.error?.data?.email_required) {
          this.reviewEmailError = err?.error?.message || 'Enter a valid customer email address before sending.';
          this.showReviewPrompt.set(true);
          return;
        }
        showToast('error', err?.error?.message || 'Review request could not be sent.');
      }
    });
  }

  private validReviewEmail(value: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  remindReviewLater() {
    const jobId = this.job()?.job_id;
    if (!jobId || this.isReviewActionSaving() || !this.reviewActionRequired()) return;
    this.isReviewActionSaving.set(true);
    this.api.remindJobReviewLater(jobId).subscribe({
      next: (res: any) => {
        this.isReviewActionSaving.set(false);
        if (!res?.success) {
          showToast('error', res?.message || 'Reminder could not be created.');
          return;
        }
        this.showReviewPrompt.set(false);
        this.job.update(current => current ? { ...current, review_request: res.data?.review_request || current.review_request } : current);
        showToast('success', 'Reminder created for you and the admin team.');
      },
      error: (err: any) => {
        this.isReviewActionSaving.set(false);
        showToast('error', err?.error?.message || 'Reminder could not be created.');
      }
    });
  }

  private loadShiftStatus() {
    this.shiftStatusLoading.set(true);
    this.api.getShiftStatus().subscribe({
      next: (res: any) => {
        this.shiftActive.set(!!(res?.success && res?.data?.active));
        this.shiftStatusLoading.set(false);
      },
      error: () => {
        this.shiftActive.set(false);
        this.shiftStatusLoading.set(false);
      }
    });
  }

  private showJobStatusResult(status: string, res: any) {
    const feedback = res?.data?.feedback_request;
    if (feedback?.enabled && feedback.email_sent) {
      showToast('success', `Job marked as ${status}. Feedback request sent.`);
      return;
    }
    if (feedback?.enabled && !feedback.email_sent) {
      showToast('error', feedback.message || `Job marked as ${status}. Feedback request was not sent.`);
      return;
    }
    showToast('success', `Job marked as ${status}`);
  }

  pauseJob() {
    const currentId = this.job()?.job_id;
    if (!currentId || this.isUpdating() || this.job()?.status !== 'In Progress') return;
    this.pauseCategory = this.pauseCategories[0];
    this.pauseRemarks = '';
    this.showPauseModal.set(true);
  }

  closePauseModal() {
    if (this.isUpdating()) return;
    this.showPauseModal.set(false);
    this.pauseRemarks = '';
  }

  confirmPauseJob() {
    const currentId = this.job()?.job_id;
    if (!currentId || this.isUpdating() || this.job()?.status !== 'In Progress' || this.pauseRemarks.trim().length < 5) return;
    this.isUpdating.set(true);
    this.api.updateJobWorkSession({
      job_id: currentId,
      action: 'pause',
      pause_category: this.pauseCategory,
      pause_remarks: this.pauseRemarks.trim()
    }).subscribe({
      next: (res: any) => {
        this.isUpdating.set(false);
        if (res.success) {
          showToast('success', res.message || 'Your timer is paused.');
          this.showPauseModal.set(false);
          this.isTimerActive.set(false);
          this.stopTimerTicking();
          this.loadJob(currentId);
        } else {
          showToast('error', res.message || 'Failed to pause job.');
        }
      },
      error: (err: any) => {
        this.isUpdating.set(false);
        showToast('error', err?.error?.message || 'Network failure pausing job.');
      }
    });
  }

  resumePausedJob() {
    const currentId = this.job()?.job_id;
    if (!currentId || this.isUpdating() || this.job()?.active_session?.status !== 'paused') return;
    if (!this.shiftActive()) {
      showToast('error', 'Start your shift before resuming your job timer.');
      return;
    }
    this.pauseResolutionNotes = '';
    this.showResumeModal.set(true);
  }

  closeResumeModal() {
    if (this.isUpdating()) return;
    this.showResumeModal.set(false);
    this.pauseResolutionNotes = '';
  }

  confirmResumePausedJob() {
    const currentId = this.job()?.job_id;
    if (!currentId || this.isUpdating() || this.job()?.active_session?.status !== 'paused' || this.pauseResolutionNotes.trim().length < 5) return;
    this.isUpdating.set(true);
    this.api.updateJobWorkSession({ job_id: currentId, action: 'resume', resume_remarks: this.pauseResolutionNotes.trim() }).subscribe({
      next: (res: any) => {
        this.isUpdating.set(false);
        if (res.success) {
          showToast('success', res.message || 'Your timer resumed.');
          this.showResumeModal.set(false);
          this.loadJob(currentId);
        } else {
          showToast('error', res.message || 'Failed to resume job.');
        }
      },
      error: (err: any) => {
        this.isUpdating.set(false);
        showToast('error', err?.error?.message || 'Network failure resuming job.');
      }
    });
  }

  canUseJobTools(): boolean {
    const currentJob = this.job();
    if (!currentJob) return false;
    if (currentJob.status === 'Paused') return false;
    return currentJob.status !== 'Scheduled' || currentJob.job_type === 'Inspection';
  }

  canStartJob(): boolean {
    const status = this.job()?.status;
    return status === 'Scheduled' || status === 'Approved';
  }

  canUseWorkerTimer(): boolean {
    return ['Scheduled', 'Approved', 'In Progress', 'Paused'].includes(this.job()?.status || '');
  }

  workerStartButtonLabel(): string {
    return this.job()?.started_at ? 'JOIN JOB & START MY TIMER' : 'START JOB & MY TIMER';
  }

  pausedDateLabel(): string {
    const value = this.job()?.pause_started_at || this.job()?.paused_at;
    if (!value) return 'Work is temporarily paused';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Work is temporarily paused';
    return `Paused since ${date.toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Pacific/Auckland' })}`;
  }

  private startTimerTicking() {
    this.stopTimerTicking();
    this.refreshTimerFromJob();
    this.intervalId = setInterval(() => this.refreshTimerFromJob(), 1000);
  }

  private stopTimerTicking() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = undefined;
    }
  }

  private refreshTimerFromJob() {
    this.timerSeconds.set(this.calculateElapsedSeconds());
  }

  private calculateElapsedSeconds(): number {
    const session = this.job()?.active_session;
    if (!session) return 0;
    let seconds = Number(session.total_seconds || 0);
    if (session.status === 'active') {
      const activeStartedAt = this.dateMs(session.active_started_at);
      if (activeStartedAt) seconds += Math.max(0, Math.floor((Date.now() - activeStartedAt) / 1000));
    }
    return seconds;
  }

  private dateMs(value: string | null | undefined): number | null {
    if (!value) return null;
    const date = new Date(value);
    const time = date.getTime();
    return Number.isNaN(time) ? null : time;
  }

  formatTime(totalSeconds: number): string {
    const h = Math.floor(Math.max(0, totalSeconds) / 3600);
    const m = Math.floor((Math.max(0, totalSeconds) % 3600) / 60);
    const s = Math.floor(Math.max(0, totalSeconds) % 60);
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }

  ngOnDestroy() {
    this.stopTimerTicking();
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }
}
